import { useState,  useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { apiRequest } from "../utils/api";
import { motion } from "framer-motion";
import { useAuthContext } from "../context/AuthContext";
import useDebouncedEffect from "../hooks/useDebouncedEffect";
import "../styles/StockInMobile.css";

const InventoryCountMobile = () => {
  const { sessionId } = useParams();
  const { loading } = useAuthContext();
  const [quantity, setQuantity] = useState(1);
  const [jan, setJan] = useState("");
  const [productName, setProductName] = useState("商品名");
  const [stock, setStock] = useState({ base: 0, counted: 0 });
  const [sku, setSku] = useState("");
  const [sessionItemId, setSessionItemId] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isReadonly, setIsReadonly] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastCount, setLastCount] = useState(null);

  const inputRef = useRef();

  const focusInputBox = () => {
    setIsReadonly(false);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // 🔍 JAN検索（棚卸用）
  const handleSearchJan = useCallback(async () => {
    if (!jan) return;

    setLastCount(null); // 最終操作表示を消す

    try {
      const response = await apiRequest(
        `inventory/sessions/${sessionId}/jan/${jan}/`,
        "GET"
      );

      setProductName(response.item_name);
      setSessionItemId(response.session_item_id);
      setSku(response.sku);
      setStock({
        base: response.base_stock,
        counted: response.counted_stock || 0,
      });
      setIsVisible(true);
      setJan("");
      setIsReadonly(false);
      focusInputBox();

    } catch (error) {
      setProductName("棚卸対象外");
      setJan("");
      focusInputBox();
    }
  }, [jan, sessionId]);

  useDebouncedEffect(() => {
    if (jan.length === 13 || jan.length === 18) {
      handleSearchJan();
      setIsReadonly(true);
    }
  }, [jan, handleSearchJan], 150);

  // ➕➖ 加減算
  const handleAdjust = async () => {
    if (!sessionItemId || isUpdating) return;

    setIsUpdating(true);

    try {
      const response = await apiRequest(
        `inventory/session-items/${sessionItemId}/`,
        "PATCH",
        { counted_stock: quantity }
      );

      setStock((prev) => ({
        ...prev,
        counted: response.counted_stock,
      }));

      setLastCount({
        productName,
        quantity,
      });

      setQuantity(1);

    } catch (error) {
      console.error("棚卸更新失敗", error);
    } finally {
      setIsUpdating(false);
      setIsReadonly(false);
      setJan("");
      focusInputBox();
    }
  };

  if (loading) return <div>読み込み中...</div>;

  return (
    <div className="stock-in-container">
      <h2>棚卸カウント</h2>

      {/* JAN入力 */}
      <div className="jan-input-area">
        <input
          ref={inputRef}
          type="text"
          inputMode="none"
          readOnly={isReadonly}
          onClick={focusInputBox}
          onChange={(e) => {
            const input = e.target.value.replace(/[^\d]/g, "");
            setJan(input);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearchJan();
              setIsReadonly(true);
            }
          }}
          value={jan}
          placeholder="JANコードをスキャン"
          autoFocus
        />
        <button
          onClick={() => {
            handleSearchJan();
            setIsReadonly(true);
          }}
        >
          検索
        </button>
      </div>

      {/* 最終操作表示 */}
      {lastCount && (
        <div className="last-stock-in-message">
          {lastCount.quantity > 0 ? "➕" : "➖"}「{lastCount.productName}」を{" "}
          {Math.abs(lastCount.quantity)} 個更新
        </div>
      )}

      {isVisible && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          className="stock-in-modal"
        >
          <div className="product-image">
            <img
              src={`/media/product_images/${sku}.jpg`}
              alt="商品画像"
            />
          </div>

          <p>JAN: {jan}</p>
          <p className="product-moji">{productName}</p>
          <p>基準在庫: {stock.base}　
            <span style={{color: "yellow",}}>
              現在カウント:{" "}
              <span
                style={{
                  color:
                    stock.counted !== stock.base ? "red" : "inherit",
                  fontWeight: "bold",
                }}
              >
                {stock.counted}
              </span>
            </span>
          </p>
          

          {/* 数量ボタン */}
          <div className="quantity-control">
            <label>数量:</label>
            <button className="quantity-button" onClick={() => setQuantity(prev => prev - 1)}>−</button>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value || "0", 10))}
            />
            <button className="quantity-button" onClick={() => setQuantity(prev => prev + 1)}>＋</button>
            <button className="quantity-button10" onClick={() => setQuantity(prev => prev + 10)}>10</button>
          </div>

          <div className="button-group">
            <button
              className="cancel-button"
              onClick={() => {
                setIsVisible(false);
                setJan("");
                focusInputBox();
              }}
            >
              ✖
            </button>
            <button className="stock-in-button" onClick={handleAdjust}>更新</button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default InventoryCountMobile;
